import type { ObjecttypeWithAttributes } from 'cadenza/api-client/dto/objecttype-dto';
import { getStore } from 'cadenza/store/cadenza-store';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { AttributeGroup } from 'cadenza/workbook/api/attribute-group';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { AttributeBundleDefinitionDto } from 'cadenza/workbook/model/attribute-bundle-definition-dto';
import type { AttributeBundleName } from 'cadenza/workbook/model/attribute-bundle-name';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import type { AttributeListController } from 'cadenza/workbook/workbook-attribute-util';
import { filterAllObjecttypes } from 'cadenza/workbook/workbook-attribute-util';
import { getAttributeById } from 'cadenza/workbook/workbook-core-selectors.state';
import { AttributeBundleValidator } from 'cadenza/workbook/workbook-panel/settings/attributes/attribute-bundle-validator';

/**
 * Messages to show for when not accepting an Attribute */
export interface FilterMessages {
  /** Error message for when attribute is unknown to workbook. */
  virtualAttributeErrorMessage?: string;
  /** Error message for when accepted categories do not match. */
  acceptedCategoriesErrorMessage?: string;
  /** Error message for when accepted types do not match. */
  acceptedAttributeTypesErrorMessage?: string;
  /** Error message for when attribute name is not allowed. */
  acceptedObjecttypeRolesErrorMessage?: string;
  /** Error message for when accepted geometry types do not match. */
  acceptedGeometryTypesErrorMessage?: string;
  /**
   * Error message for when accepted ObjectTypeId do not match up over all attributes in this bundle.
   * Measures and Dimensions always have one level while Hierarchies have more than one.
   */
  acceptedObjecttypeIdsErrorMessage?: string;
  /** Error message for when an attribute can't get added due to limits. */
  limitsReachedErrorMessage?: string;
  /** Error message for when custom predicate fails. */
  customPredicateErrorMessage?: string;
  /** Error message when an attribute is a LOB attribute without a relation. */
  lobAttributeWithoutRelationErrorMessage?: string;
}

const DEFAULT_ATTRIBUTE_BUNDLE_DEFINITION: AttributeBundleDefinitionDto = {
  attributeBundleName: '' as AttributeBundleName,
  queryRelevant: false,
  acceptedAttributeCategories: [],
  acceptedAttributeTypes: {},
  acceptedObjecttypeRoles: [],
  acceptedGeometryTypes: [],
  requiredDataSourceCapabilities: [],
  maxNumberOfLevels: Infinity,
  minNumberOfAttributes: 0,
  maxNumberOfAttributes: Infinity,
  allowCustomAggregationFunctionAttributes: true,
  toggleable: false,
};

/** A controller base class that only accepts certain Attributes given a set of filter descriptions. */
export abstract class DropController implements AttributeListController {
  readonly #validator;

  constructor() {
    this.#validator = new AttributeBundleValidator({
      attributesProvider: () => [],
    });
  }

  abstract get attributes(): WorkbookAttributeDto[];

  get(attributeId: AttributeId) {
    return getAttributeById(getStore().getState(), attributeId);
  }

  accepts(attribute: WorkbookAttributeDto, enforceAttributesNumberLimit = true) {
    return this.#validator.dropControllerAccepts(
      DEFAULT_ATTRIBUTE_BUNDLE_DEFINITION,
      attribute,
      true,
      enforceAttributesNumberLimit,
    );
  }

  get compatibleAttributesGroups(): ObjecttypeWithAttributes[] | AttributeGroup[] {
    return filterAllObjecttypes(this);
  }

  /**
   * Returns the bundle definition used for acceptance check of attributes
   *
   * @override
   * @return Object with definitions which make attribute valid drop candidate.
   */
  getBundleDefinition(): AttributeBundleDefinitionDto {
    return DEFAULT_ATTRIBUTE_BUNDLE_DEFINITION;
  }

  canAcceptMoreAttributes() {
    const maxAttributes = this.getBundleDefinition().maxNumberOfAttributes ?? Infinity;
    return this.attributes.length < maxAttributes;
  }

  /**
   * Returns all accepted AttributeIds. Empty array means all are accepted.
   * This is modeled as a non-static property as it usually is updated while changing the attributes
   * of the data structure this controller is backed by.
   *
   * @readonly
   */
  protected get acceptedAttributeIds(): (AttributeId | ActiveAttributeId)[] {
    return [];
  }

  abstract add(attributeId: AttributeId, index?: number): void;

  get dropPanelPlaceholderMessage(): string | undefined {
    return;
  }

  get viewCoverAddActionMessage(): string | undefined {
    return;
  }
}
